import React, { useContext, useState } from 'react'
import { Typography, Button } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import { PiPlugChargingBold, PiGameController, PiArrowRight } from 'react-icons/pi'
import { useIntl } from 'react-intl'

import { getRechargeTypeInfo, getRechargeTypeDisplayName } from '../../../../util/recharge-type-info'
import { ThemeContext } from '../../../../context/themeContext'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { RechargeType } from '@vacationtracker/shared/types/recharge-type'
import { RechargeTypeGame, RechargeTypeLeaderboard } from '../../../../components/recharge-type'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { RechargeDetails } from '../../../../hooks/use-energy-score'
import { rechargeTypeTracking } from '../../../../components/recharge-type/services/rechargeTypeTracking'

const { Title, Text } = Typography

interface RechargeTypeSummaryCardProps {
  rechargeDetails: RechargeDetails
  userId?: string
  companyId?: string
}

// Get summary tips based on recharge type (3 tips per type)
const getSummaryTips = (type: RechargeType, formatMessage: (descriptor: { id: string }) => string): string[] => {
  const tipKeys: Record<RechargeType, string[]> = {
    [RechargeType.DEEP_RESTORER]: ['rechargeType.energyPage.deepRestorer.tip1', 'rechargeType.energyPage.deepRestorer.tip2', 'rechargeType.energyPage.deepRestorer.tip3'],
    [RechargeType.SOCIAL_SPRINTER]: ['rechargeType.energyPage.socialSprinter.tip1', 'rechargeType.energyPage.socialSprinter.tip2', 'rechargeType.energyPage.socialSprinter.tip3'],
    [RechargeType.STRUCTURED_SOCIALIZER]: [
      'rechargeType.energyPage.structuredSocializer.tip1',
      'rechargeType.energyPage.structuredSocializer.tip2',
      'rechargeType.energyPage.structuredSocializer.tip3',
    ],
    [RechargeType.SPONTANEOUS_SOLOIST]: [
      'rechargeType.energyPage.spontaneousSoloist.tip1',
      'rechargeType.energyPage.spontaneousSoloist.tip2',
      'rechargeType.energyPage.spontaneousSoloist.tip3',
    ],
  }

  return tipKeys[type].map((key) => formatMessage({ id: key }))
}

const RechargeTypeSummaryCard: React.FC<RechargeTypeSummaryCardProps> = ({ rechargeDetails, userId, companyId }) => {
  const { formatMessage } = useIntl()
  const { theme } = useContext(ThemeContext)
  const isDark = theme === DARK_THEME

  const [showGame, setShowGame] = useState(false)
  const [showLeaderboard, setShowLeaderboard] = useState(false)

  const { rechargeType, challengeInfo } = rechargeDetails
  const info = getRechargeTypeInfo(rechargeType, formatMessage)
  const displayName = getRechargeTypeDisplayName(rechargeType, formatMessage)
  const tips = getSummaryTips(rechargeType, formatMessage)

  // Show game CTA only if the challenge is unlocked (enough colleagues completed the quiz)
  const showGameCta = challengeInfo.isUnlocked

  const handleGameClick = () => {
    if (userId && companyId) {
      rechargeTypeTracking.trackGameUnlockViewed({
        userId,
        companyId,
      })
    }
    setShowGame(true)
  }

  return (
    <>
      <div className={`recharge-summary-card ${isDark ? 'dark' : ''}`}>
        <div className="recharge-summary-header">
          <PiPlugChargingBold className="header-icon" />
          <Title level={4} className="header-title">
            <IntlMessages id="rechargeType.energyPage.title" />
          </Title>
        </div>

        <div className="recharge-summary-divider" />

        <div className="recharge-summary-type">
          <div className="type-name-row">
            <span className="type-emoji">{info.emoji}</span>
            <Text strong className="type-name">
              {displayName}
            </Text>
          </div>
          <Text className="type-tagline">{info.tagline}</Text>
        </div>

        <div className="recharge-summary-tips">
          <Text className="tips-label">
            <IntlMessages id="rechargeType.energyPage.tipsForYou" />
          </Text>
          <ul className="tips-list">
            {tips.map((tip, index) => (
              <li key={index} className="tip-item">
                <CheckOutlined className="tip-check" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        {showGameCta && (
          <>
            <div className="recharge-summary-divider" />

            <div className="recharge-summary-game-cta">
              <PiGameController className="game-icon" />
              <Text className="game-text">
                <IntlMessages id="rechargeType.energyPage.gameCta" />
              </Text>
              <Button type="link" className="game-link" onClick={handleGameClick}>
                <IntlMessages id="rechargeType.energyPage.gameCtaLink" />
                <PiArrowRight className="arrow-icon" />
              </Button>
            </div>
          </>
        )}
      </div>

      <RechargeTypeGame
        visible={showGame}
        onClose={() => setShowGame(false)}
        onShowLeaderboard={() => {
          setShowGame(false)
          setShowLeaderboard(true)
        }}
        requiredNumberOfUsers={challengeInfo.requiredNumberOfUsers}
      />

      <RechargeTypeLeaderboard
        visible={showLeaderboard}
        onClose={() => setShowLeaderboard(false)}
        onPlayGame={() => {
          setShowLeaderboard(false)
          setShowGame(true)
        }}
      />
    </>
  )
}

export default RechargeTypeSummaryCard
